/**
 * What the simulator loads: a conversation, plus the world its mocked tools answer with. The
 * simulator owns this shape but never the supply: cases arrive from whoever opened it, which keeps
 * its producers out of its imports. `simulationConfig` is keyed exactly as the run's
 * `simulation_profile`, so the same object drives the sidebar and travels on the wire unchanged.
 */

import type { ScenarioKind } from '@/types/websocket.types';
import type { SimulationProfileExtras } from '../sidebar/simulation-profile.store';
import type { SimulatorRole } from '../thread/roles';

/** Configured values that overlay the mocked tool outputs, typed per key. */
export type SimulationConfig = Partial<SimulationProfileExtras>;

export interface ScenarioCase {
  /**
   * The producing record's id, when it has one (a saved scenario does). It
   * becomes the run's conversation id, so a trace, a run and the scenario that
   * caused it all carry the same identifier instead of a fresh uuid per load.
   */
  id?: string;
  name: string;
  messages: { role: SimulatorRole; text: string }[];
  simulationConfig: SimulationConfig;
  /** The past ticket the case replays; the sidebar shows that conversation beside the run. */
  ticketId?: string;
  /** What the case's scenario is; absent on a hand-authored case. */
  kind?: ScenarioKind;
  /** The scenario's type and SOP, carried rather than re-fetched; absent on a hand-authored case. */
  sopId?: string;
  scenarioTypeId?: string;
}

/**
 * The sidebar cards a case fills. The config key's prefix already names the card
 * (`order_number` → Order), so a case never has to list its own sections.
 */
export const filledSections = (config: SimulationConfig): Set<string> =>
  new Set(
    Object.entries(config)
      .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v))
      .map(([k]) => k.split('_')[0])
  );
